import {
  ComponentProps,
  ReactElement,
  cloneElement,
  createContext,
  useContext,
  MouseEvent as ReactMouseEvent,
  useState,
  useCallback,
  useEffect,
  RefObject,
  useRef,
} from 'react'

import Icon from 'components/Icon'
import { createPortal } from 'react-dom'

import joinClassNames from 'utilities/joinClassNames'

import styles from './styles.module.scss'
import { Position } from './types'
import { calculateDropdownPosition } from './utils'

interface DropdownContextProps {
  isOpen: boolean
  onToggle: () => void
  contentRef: RefObject<HTMLUListElement>
  triggerRef: RefObject<HTMLButtonElement>
}

export const DropDownContext = createContext<DropdownContextProps>({
  isOpen: false,
  onToggle: () => null,
  contentRef: { current: null },
  triggerRef: { current: null },
})

const useDropdownContext = () => {
  const context = useContext(DropDownContext)

  if (!context) {
    throw new Error('useDropdownContext must be used within a DropdownProvider')
  }

  return context
}

interface RootProps extends ComponentProps<'div'> {
  isOpen?: boolean
}

const Root = ({ isOpen = false, children, className, ...props }: RootProps) => {
  const [open, setOpen] = useState(isOpen)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const contentRef = useRef<HTMLUListElement>(null)

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false)
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  useEffect(() => {
    triggerRef.current?.setAttribute('data-active', open ? 'true' : 'false')

    const handleClickOutside = (event: MouseEvent) => {
      const clickOnModal = (event.target as HTMLElement).closest(
        '[data-component="modal"]',
      )

      if (
        contentRef.current &&
        !contentRef.current.contains(event.target as Node) &&
        !triggerRef.current?.contains(event.target as Node) &&
        !clickOnModal
      ) {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [open])

  return (
    <DropDownContext.Provider
      value={{
        triggerRef,
        contentRef,
        isOpen: open,
        onToggle: () => setOpen((prev) => !prev),
      }}
    >
      <div {...props} className={joinClassNames(styles.root, className)}>
        {children}
      </div>
    </DropDownContext.Provider>
  )
}

type TriggerProps = ComponentProps<'button'>

const Trigger = ({ onClick, children, ...props }: TriggerProps) => {
  const { onToggle, triggerRef, isOpen } = useDropdownContext()

  const handleClick = (event: ReactMouseEvent<HTMLButtonElement>) => {
    onClick?.(event)
    onToggle()
  }

  return cloneElement(children as ReactElement, {
    onClick: handleClick,
    ref: triggerRef,
    'data-active': isOpen,
    ...props,
  })
}

interface ContentProps extends ComponentProps<'ul'> {
  offsetY?: number
  offsetX?: number
  position?: Position
}

const Content = ({
  className,
  offsetY = 4,
  offsetX = 0,
  position = 'bottom',
  ...props
}: ContentProps) => {
  const { isOpen, triggerRef, contentRef } = useDropdownContext()

  const handlePosition = useCallback(() => {
    const triggerElement = triggerRef.current
    const contentElement = contentRef.current

    if (triggerElement && contentElement) {
      const triggerRect = triggerElement.getBoundingClientRect()
      const contentRect = contentElement.getBoundingClientRect()

      const { top, left } = calculateDropdownPosition({
        triggerRect,
        contentRect,
        offsetY,
        offsetX,
        position,
      })

      contentElement.style.top = `${top + window.scrollY}px`
      contentElement.style.left = `${left + window.scrollX}px`
    }
  }, [position, offsetY, offsetX])

  useEffect(() => {
    if (isOpen) {
      handlePosition()
    }
  }, [isOpen, handlePosition])

  if (!isOpen) return null

  return createPortal(
    <ul
      ref={contentRef}
      className={joinClassNames(styles.content, styles.visible, className)}
      {...props}
    />,
    document.body,
  )
}

interface Item extends ComponentProps<'button'> {
  color?: 'element' | 'red'
}

const Item = ({ className, children, color = 'element', ...props }: Item) => (
  <li className={joinClassNames(styles.item, styles[color], className)}>
    <button {...props}>{children}</button>
  </li>
)

const Separator = ({ className, ...props }: ComponentProps<'hr'>) => (
  <hr {...props} className={joinClassNames(styles.separator, className)} />
)

const Sub = ({ children, className, ...props }: ComponentProps<'div'>) => (
  <div {...props} className={joinClassNames(styles.subMenu, className)}>
    {children}
  </div>
)

interface SubTriggerProps extends ComponentProps<'button'> {
  color?: 'element' | 'red'
}

const SubTrigger = ({
  onClick,
  children,
  className,
  color = 'element',
  ...props
}: SubTriggerProps) => {
  const { onToggle } = useDropdownContext()

  const handleClick = (event: ReactMouseEvent<HTMLButtonElement>) => {
    onClick?.(event)
    onToggle()
  }

  return (
    <li className={joinClassNames(styles.subTrigger, styles[color], className)}>
      <button onClick={handleClick} {...props}>
        {children}
        <Icon name="chevron-sm-right" color="element-default" />
      </button>
    </li>
  )
}

interface SubContentProps extends ComponentProps<'ul'> {
  position?: 'left' | 'right'
}

const SubContent = ({
  className,
  position = 'left',
  ...props
}: SubContentProps) => {
  const subContentRef = useRef<HTMLUListElement>(null)

  const handlePosition = useCallback(() => {
    const contentElement = subContentRef.current

    if (contentElement) {
      if (position === 'left') {
        subContentRef.current.style.right = '100%'
      } else {
        subContentRef.current.style.left = '100%'
      }
    }
  }, [position])

  useEffect(() => {
    handlePosition()
  }, [])

  return (
    <ul
      {...props}
      ref={subContentRef}
      className={joinClassNames(styles.subContent, className)}
    />
  )
}

const Dropdown = {
  Root,
  Trigger,
  Content,
  Separator,
  Sub,
  SubContent,
  Item,
  SubTrigger,
}

export default Dropdown
